import { PipelineStageEnum } from '@/bundles/Pipelines/types';
import { Nullable } from '@/utils/types';

/**
 * Win probability the app applies when a deal ENTERS a stage.
 *
 * `null` means "leave the stored probability untouched" and is spelled out
 * rather than omitted, so a stage missing from this map is always a bug.
 *
 * This is the single source of truth for the deal page: it seeds the value the
 * probability dialog offers AND the value that gets persisted, so the two can
 * no longer disagree. Before SCBT-182 the dialog default came from a `switch`
 * while the persisted value came from a separate if/else chain, and the two
 * contradicted each other — On Hold persisted 100 into weighted-gross
 * forecasts, Closed - Construction Pending persisted nothing, and Under
 * Contract had no value at all.
 *
 * Mirrors PROBABILITY_BY_STAGE in SlickCactus-API lib/mcp/pipelineStages.js,
 * which the MCP stage endpoint applies server-side — keep the two in sync.
 *
 * `under_contract` and `on_hold` are product decisions rather than
 * carried-over behaviour: `under_contract` had no value anywhere before and is
 * now 90; `on_hold` used to persist 100 and now leaves the stored probability
 * alone, since both 100 and 0 distort the forecast for a deal that is merely
 * paused.
 */
export const pipelineStageProbability: Record<PipelineStageEnum, Nullable<number>> = {
  [PipelineStageEnum.appointment_pending]: 5,
  [PipelineStageEnum.appointment_scheduled]: 10,
  [PipelineStageEnum.presentation_scheduled]: 20,
  [PipelineStageEnum.exclusive_pending]: 40,
  [PipelineStageEnum.exclusive_secured]: 60,
  [PipelineStageEnum.rfp_loi]: 70,
  [PipelineStageEnum.contract_lease_working]: 80,
  [PipelineStageEnum.under_contract]: 90,
  [PipelineStageEnum.on_hold]: null,
  [PipelineStageEnum.closed_won]: 100,
  [PipelineStageEnum.closed_construction_pending]: 100,
  [PipelineStageEnum.closed_lost]: 0,
};

/**
 * The options the probability dialog offers. Lives here so the map above and the
 * picker cannot drift apart — PipelineStages.vue imports it rather than keeping
 * its own copy.
 */
export const pipelineProbabilityOptions = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

/**
 * The mapped probability for a stage, or null when the stage should leave the
 * stored value alone. Unknown stages return null rather than a bogus 0.
 *
 * Takes a `number`, not `PipelineStageEnum`: the only production caller reads
 * the stage off a prop and passes a raw `parseInt()` result, so an out-of-range
 * value is exactly the input this guard exists for. Typing the parameter as the
 * enum would let TS prove the guard unreachable and make it read as dead code.
 */
export function getPipelineStageProbability (stage: number): Nullable<number> {
  const mapped = pipelineStageProbability[stage as PipelineStageEnum];

  return mapped === undefined ? null : mapped;
}
